import { computed, ref } from 'vue'
import { pwaPlatform } from '../utils/pwaPlatform.js'

// Module-level state: `beforeinstallprompt` fires once, early, so it has to be
// captured at import time and shared by every <InstallAppButton>.
const deferredPrompt = ref(null)
const installed = ref(false)
const installing = ref(false)
const accepted = ref(false)
const dismissed = ref(false)
const snoozed = ref(false)
try { snoozed.value = Number(localStorage.getItem('sf-install-snooze') || 0) > Date.now() } catch { /* Storage may be disabled. */ }
const snooze = () => { snoozed.value = true; try { localStorage.setItem('sf-install-snooze', String(Date.now()+7*86400000)) } catch {} }
const installError = ref(false)

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true)

if (typeof window !== 'undefined') {
  installed.value = isStandalone()
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt.value = e
    dismissed.value = false
    installError.value = false
  })
  window.addEventListener('appinstalled', () => {
    installed.value = true
    accepted.value = false
    deferredPrompt.value = null
  })
}

const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
const { isIOS, isAndroid, isInApp } = pwaPlatform(ua, typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0)
const isSecure = typeof window !== 'undefined' && window.isSecureContext === true

/**
 * Browsers never allow a site to put itself on the home screen silently. What
 * we can do: Chrome/Edge/Android (`canPrompt`) show the native install dialog on
 * one tap, after which the icon is added to the home screen automatically; iOS
 * Safari has no API, so `isIOS` users get a how-to instead.
 */
export function usePwaInstall() {
  const canPrompt = computed(() => !!deferredPrompt.value && !installed.value && !installing.value && !accepted.value)

  const promptInstall = async () => {
    const evt = deferredPrompt.value
    if (!canPrompt.value) return null
    // Consume the one-use event before calling the browser. prompt() runs
    // directly in the button gesture, before any awaited work.
    deferredPrompt.value = null
    installing.value = true
    installError.value = false
    dismissed.value = false
    try {
      await evt.prompt()
      const { outcome } = await evt.userChoice
      accepted.value = outcome === 'accepted' && !installed.value
      dismissed.value = outcome === 'dismissed'
      if (dismissed.value) snooze()
      return outcome
    } catch {
      installError.value = true
      return 'error'
    } finally {
      installing.value = false
    }
  }

  return { snoozed, snooze, installed, installing, accepted, dismissed, installError, canPrompt, isIOS, isAndroid, isInApp, isSecure, promptInstall }
}
